import { jsx } from "panel";
import { ButtonEl } from "common/components/button";
import { ItemsMenuEl } from "common/components/items-menu";
import { SelectTheme } from "common/components/select/constants";
import { SelectEl } from "common/components/select";
import { SkeletonListEl } from "common/components/skeleton-list";
import { SkeletonListRowSize } from "common/components/skeleton-list/types";
import { ButtonSize, ButtonTheme } from "common/components/button/constants";
import { Placement } from "common/components/tooltip/types";
import { MenuSearchBar } from "../mixins/search";
import { BetaMenuActionBar } from "../mixins/beta-action-bar";
import { SortBy } from "../properties-menu/types";
import { FilterDropdownWrapperEl } from "../filter-dropdown-wrapper";
import { createContextPanelEventHandlers } from "./context-panel";
export function template() {
  const isBetaMenu = this.isBetaMenu();
  if (!this.attr(`open`)) {
    return /* @__PURE__ */ jsx("div", {
      sel: ".mp-events-menu",
      class: { "beta-menu": isBetaMenu, "mp-child-menu": true },
      hook: { insert: (vnode) => this.helpers.setChildMenuRef(vnode) }
    });
  }
  const FilterSelectEl = () => {
    return /* @__PURE__ */ jsx(FilterDropdownWrapperEl, {
      attrs: {
        "always-show-caret": true,
        theme: SelectTheme.MenuFilter,
        size: ButtonSize.Inline,
        sections: this.getTagFilterMenuItems(),
        "menu-width": null,
        "disable-search": true
      },
      props: {
        handleFilterChange: this.helpers.handleTagFilterChange
      }
    });
  };
  const CreateItemsMenuEl = () => {
    return /* @__PURE__ */ jsx(ButtonEl, {
      sel: ".create-custom-button",
      attrs: {
        disabled: !this.helpers.canCreateItems(),
        "show-preview-tooltip": false,
        theme: ButtonTheme.MenuFooter,
        "hide-input-focus-styling": true,
        label: `Create Custom`
      },
      on: {
        click: async () => this.helpers.triggerModal()
      }
    }, /* @__PURE__ */ jsx("div", {
      sel: ".create-custom-label",
      attrs: { elref: `label` }
    }, "Create Custom"));
  };
  const InnerSearch = MenuSearchBar({
    value: this.state.searchFilter,
    handleSearchInput: this.helpers.handleSearchInput,
    backButtonClick: this.hasStagedItem() && (() => this.unstageProperty()),
    placeholder: this.helpers.getSearchPlaceholder(),
    onInsert: (el) => this.update({ keydownProxyEl: el }),
    key: `${isBetaMenu ? `beta` : `classic`}-context`,
    newStyling: isBetaMenu
  });
  const isLoading = this.helpers.isLoading();
  const hasSortFilterRow = this.canSortItems();
  return /* @__PURE__ */ jsx("div", {
    sel: ".mp-events-menu",
    class: { "beta-menu": isBetaMenu, "mp-child-menu": true },
    hook: { insert: (vnode) => this.helpers.setChildMenuRef(vnode) }
  }, InnerSearch, hasSortFilterRow && /* @__PURE__ */ jsx("div", {
    sel: ".header-filter-row"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".showing-text-wrapper"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".showing-text"
  }, this.state.searchFilter ? `Searching` : `Show`), /* @__PURE__ */ jsx(FilterSelectEl, null)), /* @__PURE__ */ jsx("div", {
    sel: ".header-extra-controls"
  }, this.helpers.shouldShowRefreshButton() && /* @__PURE__ */ jsx(ButtonEl, {
    attrs: {
      icon: `refresh`,
      disabled: isLoading,
      theme: ButtonTheme.Tertiary,
      tooltip: { text: `Refresh events`, placement: Placement.Left }
    },
    on: {
      click: this.helpers.handleRefreshButtonClick
    }
  }), /* @__PURE__ */ jsx(SelectEl, {
    attrs: {
      "always-show-caret": true,
      "icon-only": true,
      icon: {
        leftIcon: (() => {
          switch (this.state.sortByValue) {
            case SortBy.NameAsc:
              return `sort-alpha-asc`;
            case SortBy.NameDesc:
              return `sort-alpha-desc`;
            default:
              return `relevancy`;
          }
        })()
      },
      theme: SelectTheme.Tertiary,
      size: ButtonSize.Medium,
      sections: [{ label: `Sort by`, items: this.helpers.getSortingOptionSectionItems() }],
      "menu-width": null,
      tooltip: { placement: Placement.Left }
    },
    on: {
      change: this.helpers.handleSortChange
    }
  }))), isLoading ? /* @__PURE__ */ jsx(SkeletonListEl, {
    attrs: { "num-rows": 8, "row-size": SkeletonListRowSize.Large },
    sel: ".mp-events-menu-skeleton-list"
  }) : /* @__PURE__ */ jsx(ItemsMenuEl, {
    sel: ".mp-events-menu-items-menu",
    attrs: {
      "empty-message": `No items found`,
      multiple: this.attr(`multiple`),
      "search-filter": this.state.searchFilter,
      "start-focus-on-selected": true,
      ...isBetaMenu ? {
        "disable-search-sort": true,
        "disable-search-filter-matching-parts": true,
        "disable-first-item-padding": true,
        "new-styling": true
      } : {}
    },
    on: {
      click: (ev) => ev.stopPropagation(),
      clickItem: (ev) => this.handleClickItem(ev),
      clickExpanderItem: (ev) => this.handleExpandItemClick(ev),
      ...createContextPanelEventHandlers({
        track: this.trackEvent.bind(this),
        handleSelect: ({ item }) => this.triggerModal(item.value.modalType),
        handleDelete: ({ id }) => this.handleCustomEventDelete(id),
        additionalArgs: {
          "all-data-groups": this.attr(`all-data-groups`),
          "project-has-people": this.attr(`project-has-people`),
          "track-name": this.attr(`track-name`)
        }
      })
    },
    props: {
      sections: this.helpers.getMenuSections(),
      keydownProxyEl: !this.state.isModalOpen ? document.body : this.state.keydownProxyEl,
      sectionsFromInputFn: this.helpers.sectionsFromInput,
      customSearchMatcherFn: this.helpers.customSearchMatcherFn,
      contextPanelTransform: (item) => {
        if (item.value && !item.value.icon) {
          item.value.icon = item.icon;
        }
        if (item.itemFromInput) {
          item.value.itemFromInput = item.itemFromInput;
        }
        return this.getTooltipData(item.value);
      },
      contextPanelPositionParentElement: this.helpers.getChildMenuRef()
    }
  }), this.state.canUseBetaMenu ? /* @__PURE__ */ jsx(BetaMenuActionBar, {
    isBetaMenu,
    canCreateCustom: this.helpers.canCreateItems(),
    setBetaVersion: this.setBetaVersion.bind(this)
  }) : null, this.isBetaMenu() ? /* @__PURE__ */ jsx(CreateItemsMenuEl, null) : null);
}
